import { Avatar, Box, Flex, Image, Skeleton, Text } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { Response } from 'datx-jsonapi';
import { useResourcesInfinite } from 'datx-jsonapi-react';
import { FC, Fragment, useMemo } from 'react';
import { InView } from 'react-intersection-observer';

import PeopleIcon from '@/assets/icons/ic-people.svg';

import { ChatProfile } from '@/components/ui/shared/chat/ChatPopup/components/types';
import { SearchInput as SharedSearchInput } from '@/components/ui/shared/inputs/SearchInput/SearchInput';
import { useLocalSearch } from '@/hooks/use-search/useLocalSearch';
import { StringParam, useQueryParams } from '@/hooks/useQueryParams';
import { queryAllEventChatChannels } from '@/queries/event-chat-channels';
import { sessionQueryOptions } from '@/queries/upgrade/sessions';
import { EventChatChannel } from '@/resources/EventChatChannel';
import { AnyProfile, getProfileAvatar, getProfileFullName, getProfileTitle } from '@/utils/profileHelpers';
import { extractSWRInfiniteData } from '@/utils/swr/extractSWRInfiniteData';

const PAGE_SIZE = 12;

interface IChatChannelList {
  setProfileSelected: (profile: ChatProfile | null) => void;
}

export const ChatChannelList: FC<IChatChannelList> = ({ setProfileSelected }) => {
  const { searchTerm, ref: input } = useLocalSearch();
  const { data: session } = useQuery(sessionQueryOptions());
  const user = session?.user;
  const { eventId } = useQueryParams<{
    eventId: string;
  }>({
    eventId: StringParam,
  });

  const { data: eventChatChannels, error, size, setSize, isValidating } = useResourcesInfinite(
    queryAllEventChatChannels(eventId, searchTerm, PAGE_SIZE)
  );

  const { isLoadingMore, isReachingEnd, items, isEmpty } = useMemo(
    () => extractSWRInfiniteData<EventChatChannel>(eventChatChannels as Array<Response<EventChatChannel>>, error, size),
    [eventChatChannels, error, size]
  );

  if (isEmpty && !searchTerm) {
    return (
      <Flex flexGrow={1} justifyContent="center" alignItems="center">
        <Flex flexDirection="column" alignItems="center" justifyContent="space-between">
          <Flex
            justifyContent="center"
            alignItems="center"
            boxShadow="0px 8px 32px rgba(0, 0, 0, 0.08)"
            borderRadius="50%"
            w={24}
            h={24}
            textColor="violet.500"
          >
            <PeopleIcon width="48px" height="48px" />
          </Flex>
          <Text mt={7} mb={2} fontWeight="bold" fontFamily="heading" fontSize="xl">
            Connect with other attendees
          </Text>
          <Text fontFamily="heading" w="280px" textAlign="center">
            Your messages will appear here once you start chatting with someone from the People list
          </Text>
        </Flex>
      </Flex>
    );
  }

  const getOtherParticipant = (ecc: EventChatChannel) => {
    return ecc.eventChatChannelParticipants.find((p) => p.user.id !== user.id);
  };

  return (
    <Fragment>
      <SharedSearchInput
        ref={input}
        mt={6}
        w="full"
        bg="white"
        color="gray.700"
        borderRadius="full"
        placeholder="search people"
      />
      <Flex flexDirection="column" overflowY="auto" h="full" mt={5}>
        <Box w="full">
          {isEmpty && <Text mt={2}>No search results found for &quot;{searchTerm}&quot;</Text>}

          {isValidating &&
            Array.from({ length: 14 }).map((_, i) => <Skeleton mb={2} h={10} borderRadius="4px" w="100%" key={i} />)}

          {!isValidating &&
            items.map((item) => {
              const {
                user: { firstProfile: profile },
              } = getOtherParticipant(item);

              return (
                <Flex
                  alignItems="center"
                  onClick={() => setProfileSelected(profile as AnyProfile & { user?: { id: string } })}
                  _hover={{ boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.08)' }}
                  cursor="pointer"
                  mb={2}
                  key={profile.id}
                >
                  <Flex>
                    <Flex flexShrink={0}>
                      {!getProfileAvatar(profile) && (
                        <Avatar bg="violet.500" color="white" name={getProfileFullName(profile)} />
                      )}
                      {getProfileAvatar(profile) && (
                        <Image
                          src={getProfileAvatar(profile)}
                          alt={getProfileFullName(profile)}
                          width={10}
                          height={10}
                          style={{ borderRadius: '50%' }}
                        />
                      )}
                    </Flex>
                    <Flex ml={2} flexDirection="column" justifyContent="center">
                      <Text fontWeight="bold" fontFamily="heading">
                        {getProfileFullName(profile)}
                      </Text>
                      <Text fontSize="xs">{getProfileTitle(profile)}</Text>
                    </Flex>
                  </Flex>
                  {item.unreadMessages !== 0 && (
                    <Flex
                      ml="auto"
                      flexShrink={0}
                      fontSize="xs"
                      color="white"
                      borderRadius="50%"
                      bg="almostRed.500"
                      w={6}
                      h={6}
                      justifyContent="center"
                      alignItems="center"
                    >
                      {item.unreadMessages}
                    </Flex>
                  )}
                </Flex>
              );
            })}

          {isLoadingMore &&
            Array.from({ length: PAGE_SIZE }, (_, i) => (
              <Flex alignItems="center" mb={2} key={i}>
                <Skeleton borderRadius="50%" h={12} w={12} />
                <Flex ml={2} gap={2} flexDirection="column">
                  <Skeleton w={40} h={3} />
                  <Skeleton w={52} h={2} />
                </Flex>
              </Flex>
            ))}

          {!isLoadingMore && (
            <InView
              onChange={(inView) => {
                if (inView && !error && !isReachingEnd) {
                  setSize(size + 1);
                }
              }}
            >
              <Box h={1} w={1} />
            </InView>
          )}
        </Box>
      </Flex>
    </Fragment>
  );
};
